import { DateTime, Duration } from 'luxon';

import {
  ApplicationSetChildApp,
  ChildEntityStatus,
  HealthStatus,
  SyncStatus,
} from './types';

export const SUPPORT_URL =
  '/docs/default/repository/deployment-as-a-service/#support';

export const ANNOTATION_PROJECT_NAME = 'tomtom.com/daas-project-name';
export const ANNOTATION_PROJECT_HASH = 'tomtom.com/daas-project-hash';
export const ANNOTATION_APPLICATION_NAME = 'tomtom.com/daas-application-name';
export const ANNOTATION_APPLICATION_SET_NAME =
  'tomtom.com/daas-application-set-name';

export const inlineValueStyle = { lineHeight: '23px' };

export const compactHeaderStyle = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  padding: '4px 8px',
  color: 'inherit',
  opacity: 0.6,
};

export const compactCellStyle = {
  padding: '4px 8px',
};

export function formatTimestamp(ts?: string): string {
  if (!ts) return '—';
  const dt = DateTime.fromISO(ts);
  if (!dt.isValid) return ts;
  return dt.toRelative() ?? dt.toLocaleString(DateTime.DATETIME_MED);
}

export function formatDuration(seconds?: number): string {
  if (seconds === undefined || seconds === null) return '';
  const dur = Duration.fromObject({ seconds }).rescale().toObject();
  const parts: string[] = [];
  if (dur.years) parts.push(`${dur.years}y`);
  if (dur.months) parts.push(`${dur.months}mo`);
  if (dur.days) parts.push(`${dur.days}d`);
  if (dur.hours) parts.push(`${dur.hours}h`);
  if (dur.minutes) parts.push(`${dur.minutes}m`);
  if (dur.seconds && parts.length < 2)
    parts.push(`${Math.round(dur.seconds)}s`);
  return parts.slice(0, 2).join(' ');
}

export type StatusIndicatorType =
  | 'succeeded'
  | 'healthy'
  | 'synced'
  | 'failed'
  | 'degraded'
  | 'pending'
  | 'progressing'
  | 'outofsync'
  | 'suspended'
  | 'missing'
  | 'unknown';

export interface OverallStatus {
  label: string;
  type: StatusIndicatorType;
}

export function deriveOverallHealth(
  entries: ReadonlyArray<{
    creationStatus?: string;
    healthStatus?: HealthStatus;
  }>,
): OverallStatus {
  if (entries.length === 0) return { label: 'N/A', type: 'unknown' };
  const hasFailedCreation = entries.some(e => e.creationStatus === 'Failed');
  const hasDegraded = entries.some(e => e.healthStatus === 'Degraded');
  const hasPending = entries.some(e => e.creationStatus === 'Pending');
  const hasProgressing = entries.some(e => e.healthStatus === 'Progressing');
  if (hasFailedCreation || hasDegraded)
    return { label: 'Degraded', type: 'degraded' };
  if (hasPending || hasProgressing)
    return { label: 'Progressing', type: 'progressing' };
  return { label: 'Healthy', type: 'healthy' };
}

export function deriveOverallSync(
  entries: ReadonlyArray<{ syncStatus?: SyncStatus }>,
): OverallStatus {
  if (entries.length === 0) return { label: 'N/A', type: 'unknown' };
  const hasOutOfSync = entries.some(e => e.syncStatus === 'OutOfSync');
  if (hasOutOfSync) return { label: 'OutOfSync', type: 'outofsync' };
  return { label: 'Synced', type: 'synced' };
}

export type StatusBearer = ChildEntityStatus | ApplicationSetChildApp;

export function statusIndicatorType(
  status: string | undefined,
): StatusIndicatorType {
  return (status?.toLowerCase() as StatusIndicatorType) || 'unknown';
}
